import { BODY_FEELING_SCALE, bodyFeelingText } from "../lib/bodyFeeling";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, CalendarClock, CheckCircle2, RotateCcw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { dayjs, dayKey, nowInTz } from "../lib/time";
import MealReplacementSheet from "../components/MealReplacementSheet";
import PlayerNutritionSummary from "../components/PlayerNutritionSummary";
import { isFutureMeal, isMealItem, isSupplementItem, mealNutrition, playerItemStatusKey, playerItemTitle, playerItemTone } from "../lib/playerSchedulePresentation";
import PlayerNavigation from "../components/PlayerNavigation";
import { dailyCompletedItems } from "../lib/dailyCompletedItems";
import "../player-training-actions.css";

export default function PlayerDailyCheck() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedDate = searchParams.get("date") || nowInTz(user?.timezone || "UTC").format("YYYY-MM-DD");
  const [state, setState] = useState("loading");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const correctionInFlight = useRef(new Set());
  const [correcting, setCorrecting] = useState(new Set());
  const [error, setError] = useState("");
  const [feedbacks, setFeedbacks] = useState({});
  const [currentItems, setCurrentItems] = useState([]);
  const [displayItems, setDisplayItems] = useState(null);
  const [serverNow, setServerNow] = useState(Date.now());
  const serverClock = useRef({ time: Date.now(), receivedAt: Date.now() });
  const [mealSheet, setMealSheet] = useState(null);
  const requestSequence = useRef(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setServerNow(serverClock.current.time + Math.max(0, Date.now() - serverClock.current.receivedAt));
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setState("loading"); setError("");
    try {
      const { data } = await api.get("/me/daily-summary", { params: { day: requestedDate } });
      if (sequence !== requestSequence.current) return;
      setCurrentItems(data.current_items || []);
      setDisplayItems(data.display_items ?? data.daily_summary?.display_items ?? null);
      const receivedAt = Date.now();
      const time = Date.parse(data.server_time) || receivedAt;
      serverClock.current = { time, receivedAt };
      setServerNow(time);
      setSummary(data.daily_summary); setState("ready");
    } catch (err) {
      if (sequence !== requestSequence.current) return;
      setError(apiError(err.response?.data?.detail)); setState("error");
    }
  }, [requestedDate]);
  useEffect(() => { load(); return () => { requestSequence.current += 1; }; }, [load]);

  const correct = async (item, correction) => {
    const id = item.schedule_item_id;
    if (correctionInFlight.current.has(id)) return;
    correctionInFlight.current.add(id);
    setCorrecting(new Set(correctionInFlight.current)); setError("");
    try {
      await api.post(`/me/schedule-items/${item.schedule_item_id}/correction`, {
        correction,
      });
      await load();
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally {
      correctionInFlight.current.delete(id);
      setCorrecting(new Set(correctionInFlight.current));
    }
  };

  const feedbackFor = (id) => feedbacks[id] || {
    exertion: 3, body_feeling: 3, body_feeling_scale: BODY_FEELING_SCALE, injury: false, injury_notes: "",
  };
  const setFeedback = (id, field, value) => setFeedbacks((current) => ({
    ...current, [id]: { ...feedbackFor(id), [field]: value },
  }));
  const submitFeedback = async (item) => {
    setBusy(item.schedule_item_id); setError("");
    try {
      await api.post(`/me/schedule-items/${item.schedule_item_id}/training-feedback`, feedbackFor(item.schedule_item_id));
      await load();
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const rows = dailyCompletedItems(summary, currentItems, displayItems);
  const noValidActions = summary?.score_eligible === false || summary?.eligible_item_count === 0
    || summary?.empty_state === "no_valid_actions"
    || (Array.isArray(summary?.items) && summary.items.length === 0 && summary.score_eligible !== true);
  const hasScore = !noValidActions && typeof summary?.performance_score === "number" && Number.isFinite(summary.performance_score);

  return (
    <div className="pc-player-ui pc-player-daily-check pc-player-mobile-scroll min-h-screen safe-bottom px-5 py-6" data-voice-dock="true" style={{ background: "var(--pc-black)" }}>
      <button className="pc-btn pc-btn-ghost flex items-center gap-2" onClick={() => nav("/today")}>
        <ArrowLeft size={16} /> {t("today")}
      </button>
      <header className="mt-6">
        <h1 className="text-3xl font-display font-bold">{t("dailyCheck")}</h1>
        <p className="text-sm mt-1 font-semibold" style={{ color: "var(--pc-cyan)" }} data-testid="daily-check-date">
          {dayjs(summary?.local_date || requestedDate).locale(lang).format("dddd, D MMMM YYYY")}
        </p>
        <p className="text-sm mt-1" style={{ color: "var(--pc-muted)" }}>{t("dailyCheckCurrentHelp")}</p>
      </header>

      {state === "loading" && <div className="flex justify-center py-24"><div className="pc-spinner" /></div>}
      {state === "error" && <button className="pc-btn pc-btn-ghost mt-6" onClick={load}>{t("retry")}</button>}
      {state === "ready" && !summary && <p className="text-sm mt-6">{t("noDailySummary")}</p>}
      {state === "ready" && summary && (
        <main className="mt-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="pc-card p-4">
              <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("performanceScore")}</p>
              <p className="text-3xl font-display font-bold mt-1" data-testid="daily-check-score">
                {hasScore ? `${Math.round(summary.performance_score)}%` : "—"}
              </p>
              {!hasScore && <p className="text-xs mt-2" style={{ color: "var(--pc-muted)" }}>{t("noScorableActivities")}</p>}
            </div>
            <div className="pc-card p-4">
              <p className="text-xs" style={{ color: "var(--pc-muted)" }}>{t("gamepowerAdded")}</p>
              <p
                className="text-3xl font-display font-bold mt-1"
                data-testid="daily-check-gamepower-added"
              >
                +{Math.round(summary.gamepower_added || 0)} %
              </p>
            </div>
          </div>
          <p className="text-xs mt-4" style={{ color: "var(--pc-muted)" }}>{t("dailyScoreSnapshot")}</p>
          <h2 className="text-sm font-semibold mt-5">{t("dailyCurrentItems")}</h2>
          <div className="space-y-3 mt-3">
            {rows.length === 0 && <p className="text-sm" style={{ color: "var(--pc-muted)" }} data-testid="daily-check-empty">{t("noCompletedActivities")}</p>}
            {rows.map((item) => {
              const tone = playerItemTone(item, serverNow);
              const StatusIcon = tone === "done" ? CheckCircle2 : tone === "missed" ? RotateCcw : CalendarClock;
              const recoveryItem = ["recovery", "physio"].includes((item.category || "").toLowerCase());
              const itemBusy = busy === item.schedule_item_id || correcting.has(item.schedule_item_id);
              return (
              <div key={item.schedule_item_id} className="pc-card p-4" data-testid={`daily-item-${item.schedule_item_id}`}>
                <div className="flex gap-3 items-start">
                  <StatusIcon size={20} color={tone === "missed" ? "var(--pc-score-medium)" : "var(--pc-muted)"} className="shrink-0 mt-1" />
                  <div className="flex-1 min-w-0" style={{ overflowWrap: "anywhere" }}>
                    <p className="font-semibold">{playerItemTitle(item, t)}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{t(playerItemStatusKey(item, serverNow))}</p>
                    {isMealItem(item) && <p className="text-xs mt-2"><PlayerNutritionSummary nutrition={mealNutrition(item)} /></p>}
                    {(item.allowed_actions || []).includes("submit_match_feedback") && <button className="pc-btn pc-btn-ghost mt-3" onClick={() => nav(`/today?date=${dayKey(item.start_at, user?.timezone || "UTC")}&item=${item.id}`)}>{t("giveFeedback")}</button>}
                    {(item.category === "training" || item.category === "workout") && item.training_feedback && (
                      <p className="text-xs mt-3" style={{ color: "var(--pc-score-good)" }}>
                        {t("feedbackSaved")}: {t("exertion")} {item.training_feedback.exertion}, {t("bodyFeeling")} {bodyFeelingText(item.training_feedback, t)}
                      </p>
                    )}
                    {(item.allowed_actions || []).includes("submit_feedback") && !item.training_feedback && !item.training_feedback_id && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <label className="text-xs" style={{ color: "var(--pc-muted)" }}>
                          {t("exertion")}
                          <select className="pc-input mt-1" value={feedbackFor(item.schedule_item_id).exertion}
                            onChange={(event) => setFeedback(item.schedule_item_id, "exertion", Number(event.target.value))}>
                            {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value}</option>)}
                          </select>
                        </label>
                        <label className="text-xs" style={{ color: "var(--pc-muted)" }}>
                          {t("bodyFeeling")}
                          <select className="pc-input mt-1" value={feedbackFor(item.schedule_item_id).body_feeling}
                            onChange={(event) => setFeedback(item.schedule_item_id, "body_feeling", Number(event.target.value))}>
                            {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value} – {t(`bodyFeelingMeaning_${value}`)}</option>)}
                          </select>
                        </label>
                        <label className="col-span-2 text-sm flex items-center gap-2">
                          <input type="checkbox" checked={feedbackFor(item.schedule_item_id).injury}
                            onChange={(event) => setFeedback(item.schedule_item_id, "injury", event.target.checked)} />
                          {t("injuryProblem")}
                        </label>
                        {feedbackFor(item.schedule_item_id).injury && (
                          <input className="pc-input col-span-2" placeholder={t("injuryNote")}
                            value={feedbackFor(item.schedule_item_id).injury_notes}
                            onChange={(event) => setFeedback(item.schedule_item_id, "injury_notes", event.target.value)} />
                        )}
                        <button className="pc-btn pc-btn-primary col-span-2" disabled={itemBusy}
                          onClick={() => submitFeedback(item)}>{t("saveFeedback")}</button>
                      </div>
                    )}
                    <div className={`flex flex-wrap gap-2 mt-3 ${["training", "workout"].includes(item.category) || recoveryItem ? "pc-player-training-action-row" : ""}`}>
                      {(item.allowed_actions || []).includes("correct_intake") && isMealItem(item) && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost flex items-center gap-1"
                          onClick={() => correct(item, "as_planned")}>
                          <RotateCcw size={14} /> {t("didEatAfterAll")}
                        </button>
                      )}
                      {(item.allowed_actions || []).includes("correct_intake") && isSupplementItem(item) && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost flex items-center gap-1"
                          onClick={() => correct(item, "as_planned")}>
                          <RotateCcw size={14} /> {t("didTakeAfterAll")}
                        </button>
                      )}
                      {(item.allowed_actions || []).includes("mark_not_eaten") && !isFutureMeal(item, serverNow) && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost"
                          onClick={() => correct(item, "not_eaten")}>{t("notEaten")}</button>
                      )}
                      {(item.allowed_actions || []).includes("report_external_alternative") && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost"
                          onClick={() => setMealSheet(item)}>{t(isFutureMeal(item, serverNow) ? "exchangeMeal" : "alternativeMeal")}</button>
                      )}
                      {(item.allowed_actions || []).includes("mark_not_taken") && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost flex items-center gap-1"
                          onClick={() => correct(item, "forgotten")}>
                          <RotateCcw size={14} /> {t("forgotten")}
                        </button>
                      )}
                      {(item.allowed_actions || []).includes("mark_missed") && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost flex items-center gap-1"
                          onClick={() => correct(item, "missed")}
                          data-testid={recoveryItem ? `recovery-correction-${item.schedule_item_id}` : undefined}>
                          <RotateCcw size={14} /> {t("missed")}
                        </button>
                      )}
                      {(item.allowed_actions || []).includes("correct_recovery") && (
                        <button disabled={itemBusy} className="pc-btn pc-btn-ghost flex items-center gap-1"
                          onClick={() => correct(item, "as_planned")} data-testid={`recovery-correction-${item.schedule_item_id}`}>
                          <RotateCcw size={14} /> {t("markRecoveryCompleted")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ); })}
          </div>
          <button className="pc-btn pc-btn-ghost mt-4" data-testid="daily-check-review-day"
            onClick={() => nav(`/today?date=${summary.local_date || requestedDate}`)}>{t("reviewDayActivities")}</button>
          {error && <p className="text-sm mt-4" style={{ color: "var(--pc-score-poor)" }}>{error}</p>}
        </main>
      )}
      {mealSheet && <MealReplacementSheet item={mealSheet} future={isFutureMeal(mealSheet, serverNow)} onClose={() => setMealSheet(null)} onSaved={load} />}
      <PlayerNavigation dockedVoice active="today" voiceContext={{ type: "other", title: t("dailyCheck") }}
        onVoiceSent={(request) => nav(`/requests?request=${request.id}`)} />
    </div>
  );
}
